import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Shield,
  X,
  Mail,
} from 'lucide-react';

interface AdminProfile {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  role: string;
  is_active?: boolean;
}

export function SuperAdminManagement() {
  const { profile } = useAuth();
  const { setPageTitle, setHeaderAction } = useAdminLayoutContext();
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminProfile | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      fullName: '',
      phone: '',
    });
  };

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Superadmin brukere');
    setHeaderAction(
      <button
        onClick={() => {
          resetForm();
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
      >
        <Plus className="h-5 w-5" />
        Ny administrator
      </button>
    );
    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setPageTitle, setHeaderAction]);

  // Fetch superadmins only
  const { data: administrators, loading: adminsLoading, refetch } = useSupabaseQuery<AdminProfile[]>(
    'profiles',
    {
      select: '*',
      filter: (q: any) => q.eq('role', 'superadmin'),
      order: { column: 'full_name', ascending: true },
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingAdmin) {
        // Update existing superadmin
        const { error } = await supabase
          .from('profiles')
          .update({
            full_name: formData.fullName,
            phone: formData.phone || null,
            role: 'superadmin',
          })
          .eq('id', editingAdmin.id);

        if (error) throw error;
      } else {
        // Create new superadmin via RPC
        const { data, error } = await supabase.rpc('create_user_with_profile', {
          p_user_id: crypto.randomUUID(),
          p_email: formData.email,
          p_password: formData.password,
          p_full_name: formData.fullName,
          p_phone: formData.phone || null,
          p_role: 'superadmin',
          p_department_id: null,
          p_company_id: null,
          p_is_active: true,
        });

        if (error) throw error;
        if (data && data.success === false) {
          throw new Error(data.error || 'Ukjent feil ved opprettelse');
        }
      }

      setShowModal(false);
      setEditingAdmin(null);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error('Error saving admin:', error);
      const msg = error.message?.includes('users_email_partial_key')
        ? 'En bruker med denne e-postadressen finnes allerede.'
        : error.message || 'Ukjent feil';
      alert(`Feil ved lagring: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (admin: AdminProfile) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: '',
      fullName: admin.full_name || '',
      phone: admin.phone || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (admin: AdminProfile) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${admin.full_name || admin.email}?`)) {
      return;
    }

    try {
      const { error } = await supabase.from('profiles').delete().eq('id', admin.id);
      if (error) throw error;
      refetch();
    } catch (error: any) {
      console.error('Error deleting admin:', error);
      alert(`Feil ved sletting: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingAdmin(null);
    resetForm();
  };

  // Only superadmins can access this page
  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">Du har ikke tilgang til denne siden.</p>
        </div>
      </div>
    );
  }

  const adminList = administrators || [];

  return (
    <div className="space-y-6">
      {/* Administrators List */}
      {adminList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Shield className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-2">Ingen system-administratorer registrert</p>
          <p className="text-gray-500 text-sm">Dette er backend-administratorer som kan administrere hele systemet</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {adminList.map((admin) => (
            <div
              key={admin.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
                      {admin.full_name || 'Ikke satt'}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                      <Mail className="w-4 h-4" />
                      {admin.email}
                    </div>
                    {admin.phone && (
                      <p className="text-sm text-gray-500 mt-1">
                        {admin.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(admin)}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    title="Rediger"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(admin)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Slett"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingAdmin ? 'Rediger administrator' : 'Ny administrator'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-post *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!!editingAdmin}
                />
              </div>

              {/* Password (only for new) */}
              {!editingAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passord *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fullt navn *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Lagrer...' : editingAdmin ? 'Lagre endringer' : 'Opprett administrator'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
